import { cloneDeep, isEqual } from 'lodash';
import React, { Component } from 'react';
import { timeParse, timeFormat } from 'd3';

import { session } from 'services/session';
import { api } from 'services/api';
import { dateService } from 'services/date';
import analyticService from 'services/analytics';
import megatableService from './megatable-service';

import { Loader, NoResult } from '../../misc/loader-utils';
import Graph from './graph';

import './megatable-graph.scss';

class MegatableGraph extends Component {
	constructor(props) {
		super(props);

		this.Api = api;
		this.dateService = dateService;
		this.AnalyticService = analyticService;
		this.service = megatableService;

		this.statType = 'article_graph';
		this.chartOptions = [
			{
				label: 'Clicks',
				statKey: 'clicks',
				dataType: 'number',
				d3Format: '',
			},
			{
				label: 'Revenue',
				statKey: 'total_attributed_revenue',
				dataType: 'currency',
				d3Format: '$',
			},
		];

		if (session.isOrgAdmin && session.userRole !== 'merchants') {
			this.chartOptions.unshift({
				label: 'Earnings',
				statKey: 'publisher_total_earnings',
				dataType: 'currency',
				d3Format: '$',
			});
		}
		this.graphColors = ['green', 'purple', 'orange'];

		// Helpers
		this.parseDate = timeParse('%Y-%m-%d');
		this.formatDate = timeFormat('%Y-%m-%d');

		this.state = {
			chartOptions: session.userRole === 'merchants'
				? this.chartOptions[0]
				: this.chartOptions[1],
			processedStats: [],
			xTicks: [], // An array of ticks, format: moment object
			loading: true,
			noResult: false,
		};
	}

	componentDidUpdate(prevProps) {
		const paramChanged = (attr) => !isEqual(prevProps[attr], this.props[attr]);

		if (paramChanged('stats') || paramChanged('granularity')) {
			this.setState({loading: true});
			if (this.props.stats) {
				this.processEditStats();
			}
		}
	}

	processEditStats() {
		// Shallow copying leads to mutations to props
		let processedStats = cloneDeep(this.props.stats);
		processedStats = processedStats.map((data, index) => {
			return Object.assign(data, {color: this.graphColors[index]});
		});

		const granularity = this.props.granularity;
		// Backend returns all monthly stats rounded down to the 1st, replace with correct date
		if (granularity === 'monthly' || granularity === 'yearly') {
			processedStats = this.fixMonthlyStart(processedStats);
		}
		const xTicks = this.getXTicks(granularity);
		if (granularity === 'weekly' || granularity === 'yearly') {
			processedStats = this.aggregateStats(processedStats, granularity, xTicks);
		}

		this.setState({
			processedStats: processedStats,
			xTicks: xTicks,
			loading: false,
			noResult: !processedStats.length,
		});
	}

	fixMonthlyStart(allStats) {
		/*
		 * Replace edit start date with user-selected date_from
		 * if truncated dates (YYYY-MM) match. Ex: 2019-04-01 becomes 2019-04-08
		 */
		allStats.forEach((editStats, index) => {
			const formattedDateFrom = this.props.dates.date_from.slice(0, 7);
			const formattedEditStartDate = editStats.data[0].event_date.slice(0, 7);

			if (formattedDateFrom === formattedEditStartDate) {
				allStats[index].data[0].event_date = this.props.dates.date_from;
			}
		});
		return allStats;
	}

	getXTicks(granularity) {
		/*
		 * By determining the x-axis ticks in megatable-graph,
		 * we can more clearly aggregate the stats
		 */
		const startDate = this.props.dates.date_from;
		const endDate = this.props.dates.date_to;
		const xTicks = [startDate];
		const offsetUnit = granularity === 'daily' ?
			'days' : granularity.slice(0, -2) + 's';

		let date = this.dateService.offsetFromDate(startDate, 1, offsetUnit);

		// Continue to add ticks until we've exceeded our date range
		while (this.dateService.compareDates(date, endDate) <= 0) {
			const adjustedDate = this.getGranularityStartDate(cloneDeep(date), granularity);
			xTicks.push(adjustedDate);
			date = this.dateService.offsetFromDate(date, 1, offsetUnit);
		}

		return xTicks.map((date) => this.parseDate(date));
	}

	aggregateStats(allStats, granularity, xTicks) {
		/*
		 * Create buckets that represent the time period between each set of 2 ticks.
		 * As we iterate through the stats, we place them in the bucket they belong
		 * in by comparing dates. Then, aggregate each bucket of stats together.
		 */
		const aggregateStats = [];

		allStats.forEach((obj) => {
			const dateBuckets = xTicks.map((tick) => {
				return {
					date: tick,
					stats: [],
				};
			});

			const stats = obj.data;
			let bucketIndex = 0;

			for (let i = 0; i < stats.length; i++) {
				const currentDate = dateBuckets[bucketIndex].date;
				const nextDate = dateBuckets[bucketIndex + 1] ?
					dateBuckets[bucketIndex + 1].date : null;
				const currentDateComparison = this.dateService.compareDates(
					currentDate, stats[i].event_date
				);
				const nextDateComparison = this.dateService.compareDates(
					nextDate, stats[i].event_date
				);

				if (currentDateComparison <= 0 && (nextDateComparison > 0 || !nextDate)) {
					dateBuckets[bucketIndex].stats.push(stats[i]);
				} else {
					bucketIndex++;
					i--;
				}
			}
			aggregateStats.push(this.aggregateByDate(dateBuckets));
		});

		return allStats.map((obj, i) => {
			obj.data = aggregateStats[i];
			return obj;
		});
	}

	aggregateByDate(buckets) {
		const statArray = buckets.map((bucket) => {
			if (!bucket.stats.length) return null;

			const stats = bucket.stats;
			const aggregateStat = (key) => stats.reduce((agg, stat) => {
				return agg += parseFloat(stat[key]);
			}, 0);
			return {
				pub_id: stats[0].pub_id,
				event_date: this.formatDate(bucket.date),
				clicks: aggregateStat('clicks'),
				publisher_total_earnings: aggregateStat('publisher_total_earnings'),
				total_attributed_revenue: aggregateStat('total_attributed_revenue'),
			};
		});
		return statArray.filter((data) => data);
	}

	selectChartOption(chartOption) {
		this.setState({chartOptions: chartOption}, () => {
			const gaLabel = `${this.props.eventPrefix} Y-Axis Dimension ${chartOption.label}`;
			this.AnalyticService.trackEvent('Mega Tables', 'Click', gaLabel);
		});
	}

	getGranularityStartDate(dateObject, granularity) {
		// weekly => week, monthly => month, yearly => year
		const timeUnit = granularity.slice(0, -2);
		return this.dateService.formatDate(dateObject.startOf(timeUnit).startOf('day'));
	}

	render() {
		const loadingStatus = this.state.loading;
		const noResult = this.state.noResult;

		const chartOptions = this.chartOptions.map((chartOption, i) => {
			const selected = this.state.chartOptions.statKey === chartOption.statKey;
			return (
				<div key={i}
					className={`chart-btn ${selected ? 'selected' : ''}`}
					onClick={() => this.selectChartOption(chartOption)}>
					{chartOption.label}
				</div>
			);
		});
		const chartSelection = !loadingStatus && !noResult ? (
			<div className="chart-selection">
				<span>Chart:</span>
				{chartOptions}
			</div>
		) : null;

		const graph = !loadingStatus && !noResult ? (
			<Graph
				stats={this.state.processedStats}
				xTicks={this.state.xTicks}
				chartOptions={this.state.chartOptions}
				dates={this.props.dates}
				granularity={this.props.granularity}
			/>
		) : null;

		return (
			<div className="megatable-graph">
				{chartSelection}
				{graph}
				<Loader loading={loadingStatus} />
				<NoResult
					noResult={noResult && !loadingStatus}
					type={this.statType}
				/>
			</div>
		);
	}
}

export default MegatableGraph;
